// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import {
    convertDateOnlyStringToDate,
    convertDateToDateOnlyString,
} from "@insite/client-framework/Common/Utilities/DateUtilities";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import updatePreviouslyPurchasedFilters from "@pace/App/Handlers/xPreviouslyPurchased/UpdatePreviouslyPurchasedFilters";
import {
    PreviouslyPurchasedFilters as IPreviouslyPurchasedFilters,
    PreviouslyPurchasedFilters,
} from "@pace/App/Services/PreviouslyPurchasedService";
import { CustomAppState } from "@pace/App/Store/Reducers";
import { customXngageWidget } from "@pace/XDL/constants";
import debounce from "lodash/debounce";
import React, { FC, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    previouslyPurchasedFilters: state.xPreviouslyPurchasedFilterList.previouslyPurchasedFilters,
});

const mapDispatchToProps = {
    updatePreviouslyPurchasedFilters,
};

interface OwnProps {
    isFiltersOpened: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PreviouslyPurchasedFilterStyles {
    container?: GridContainerProps;
    partNumberGridItem?: GridItemProps;
    partNumberTextField?: TextFieldPresentationProps;
    customerPartNumberGridItem?: GridItemProps;
    customerNumberTextField?: TextFieldPresentationProps;
    manufactureNumberGridItem?: GridItemProps;
    manufactureNumberTextField?: TextFieldPresentationProps;
    rangeWrapper?: GridItemProps;
    fromDate?: DatePickerPresentationProps;
    toDate?: DatePickerPresentationProps;
    fromQty?: TextFieldPresentationProps;
    toQty?: TextFieldPresentationProps;
    buttonsItem?: GridItemProps;
    clearFiltersButton?: ButtonPresentationProps;
    appliedFiltersContainer?: GridContainerProps;
    appliedFilterTag?: TagPresentationProps;
}

export const filterStyles: PreviouslyPurchasedFilterStyles = {};

export const styles = filterStyles;

const PreviouslyPurchasedFilters: FC<Props> = ({
    updatePreviouslyPurchasedFilters,
    previouslyPurchasedFilters: { filters },
    isFiltersOpened,
}) => {
    // Locally state so we do debounce changes on the redux state as it will trigger a BE call
    const [_filters, _setFilters] = useState<PreviouslyPurchasedFilters>({});
    const debouncedUpdatePreviouslyPurchasedFilters = useMemo(
        () => debounce(updatePreviouslyPurchasedFilters, 250),
        [updatePreviouslyPurchasedFilters],
    );

    const onClearFilters = () => {
        if (Object.keys(_filters).length === 0) {
            // already cleared
            return;
        }
        _setFilters({});
        debouncedUpdatePreviouslyPurchasedFilters({
            type: "Replace",
        });
    };

    const onDeleteFilter = (filterKey: string) => {
        _setFilters(filters => {
            const copyFilters = { ...filters };
            delete copyFilters[filterKey as keyof typeof filters];
            return copyFilters;
        });
        debouncedUpdatePreviouslyPurchasedFilters({
            filters: {
                [filterKey]: undefined,
            },
        });
    };

    const onChangeFilter = (filterKey: string, value: any) => {
        if (_filters[filterKey as keyof typeof filters] === value) {
            return;
        }
        _setFilters(filters => ({ ...filters, [filterKey]: value }));

        debouncedUpdatePreviouslyPurchasedFilters({
            filters: {
                [filterKey]: value,
            },
        });
    };
    if (isFiltersOpened === false) {
        return <SearchTags {..._filters} onDelete={onDeleteFilter} />;
    }

    const fromDateObj = convertDateOnlyStringToDate(_filters.fromDate);
    const toDateObj = convertDateOnlyStringToDate(_filters.toDate);

    return (
        <>
            <GridContainer {...styles.container}>
                <GridItem {...styles.partNumberGridItem}>
                    <TextField
                        {...styles.partNumberTextField}
                        value={_filters.pacePartNumber || ""}
                        label={translate("Part #")}
                        data-test-selector="previouslyFilters_pacePartNumber"
                        onChange={event => onChangeFilter("pacePartNumber", event.target.value)}
                        placeholder={translate("Part #")}
                    />
                </GridItem>
                <GridItem {...styles.customerPartNumberGridItem}>
                    <TextField
                        {...styles.customerNumberTextField}
                        value={_filters.customerProductNumber || ""}
                        label={translate("CPN #")}
                        data-test-selector="previouslyFilters_customerPartNumber"
                        onChange={event => onChangeFilter("customerProductNumber", event.target.value)}
                        placeholder={translate("CPN #")}
                    />
                </GridItem>
                <GridItem {...styles.manufactureNumberGridItem}>
                    <TextField
                        {...styles.manufactureNumberTextField}
                        value={_filters.manufacturerNumber || ""}
                        label={translate("MFG #")}
                        data-test-selector="previouslyFilters_pacePartNumber"
                        onChange={event => onChangeFilter("manufacturerNumber", event.target.value)}
                        placeholder={translate("MFG #")}
                    />
                </GridItem>
                <GridItem {...styles.rangeWrapper}>
                    <VisuallyHidden as="label">{translate("Date Range")}</VisuallyHidden>
                    <DatePicker
                        {...styles.fromDate}
                        label={translate("From")}
                        selectedDay={fromDateObj}
                        onDayChange={state =>
                            onChangeFilter("fromDate", convertDateToDateOnlyString(state.selectedDay))
                        }
                        dateTimePickerProps={{
                            clearIcon: null,
                            maxDate: toDateObj || new Date(),
                            ...styles.fromDate?.dateTimePickerProps,
                        }}
                    />
                    <DatePicker
                        {...styles.toDate}
                        label={translate("To")}
                        selectedDay={toDateObj}
                        onDayChange={state => onChangeFilter("toDate", convertDateToDateOnlyString(state.selectedDay))}
                        dateTimePickerProps={{
                            clearIcon: null,
                            minDate: fromDateObj,
                            maxDate: new Date(),
                            ...styles.toDate?.dateTimePickerProps,
                        }}
                    />
                </GridItem>
                <GridItem {...styles.rangeWrapper}>
                    <VisuallyHidden as="label">{translate("Qty Range")}</VisuallyHidden>
                    <TextField
                        {...styles.fromQty}
                        type="qty"
                        value={_filters.fromQty || ""}
                        min={0}
                        label={translate("From Qty")}
                        onChange={e => onChangeFilter("fromQty", e.target.value)}
                    />
                    <TextField
                        {...styles.toQty}
                        type="qty"
                        min={_filters.fromQty}
                        value={_filters.toQty || ""}
                        label={translate("To Qty")}
                        onChange={e => onChangeFilter("toQty", e.target.value)}
                    />
                </GridItem>
                <GridItem {...styles.buttonsItem}>
                    <Button
                        {...styles.clearFiltersButton}
                        data-test-selector="previouslyFilters_clearFiltersButton"
                        onClick={onClearFilters}
                        aria-label="Clear Filters"
                    >
                        {translate("Clear Filters")}
                    </Button>
                </GridItem>
            </GridContainer>
            <SearchTags {...filters} onDelete={onDeleteFilter} />
        </>
    );
};

const SearchTags = ({
    fromDate,
    toDate,
    pacePartNumber,
    manufacturerNumber,
    customerProductNumber,
    fromQty,
    toQty,
    onDelete,
}: IPreviouslyPurchasedFilters & { onDelete: (filterName: string) => void }) => {
    return (
        <StyledWrapper {...styles.appliedFiltersContainer}>
            {fromDate && (
                <Tag
                    {...styles.appliedFilterTag}
                    data-test-selector="previouslyPurchasedFilters_fromDate"
                    onDelete={() => onDelete("fromDate")}
                >
                    {`${translate("From")}: ${fromDate}`}
                </Tag>
            )}
            {toDate && (
                <Tag
                    {...styles.appliedFilterTag}
                    data-test-selector="previouslyPurchasedFilters_toDate"
                    onDelete={() => onDelete("toDate")}
                >
                    {`${translate("To")}: ${toDate}`}
                </Tag>
            )}
            {pacePartNumber && (
                <Tag
                    {...styles.appliedFilterTag}
                    data-test-selector="previouslyPurchasedFilters_pacePartNumber"
                    onDelete={() => onDelete("pacePartNumber")}
                >
                    {`${translate("Part Number")}: ${pacePartNumber}`}
                </Tag>
            )}
            {manufacturerNumber && (
                <Tag
                    {...styles.appliedFilterTag}
                    data-test-selector="previouslyPurchasedFilters_manufacturerNumber"
                    onDelete={() => onDelete("manufacturerNumber")}
                >
                    {`${translate("MFG")}: ${manufacturerNumber}`}
                </Tag>
            )}
            {fromQty && (
                <Tag
                    {...styles.appliedFilterTag}
                    data-test-selector="previouslyPurchasedFilters_fromQty"
                    onDelete={() => onDelete("fromQty")}
                >
                    {`${translate("Min Qty")}: ${fromQty}`}
                </Tag>
            )}
            {toQty && (
                <Tag
                    {...styles.appliedFilterTag}
                    data-test-selector="previouslyPurchasedFilters_toQty"
                    onDelete={() => onDelete("toQty")}
                >
                    {`${translate("Max Qty")}: ${toQty}`}
                </Tag>
            )}
            {customerProductNumber && (
                <Tag
                    {...styles.appliedFilterTag}
                    data-test-selector="previouslyPurchasedFilters_customerProductNumber"
                    onDelete={() => onDelete("customerProductNumber")}
                >
                    {`${translate("CPN")}: ${customerProductNumber}`}
                </Tag>
            )}
        </StyledWrapper>
    );
};

export const PreviouslyPurchasedFiltersComponent = connect(
    mapStateToProps,
    mapDispatchToProps,
)(PreviouslyPurchasedFilters);

const widgetModule: WidgetModule = {
    component: PreviouslyPurchasedFiltersComponent,
    definition: {
        group: customXngageWidget as any,
        displayName: "Previously Purchased Filter",
        allowedContexts: ["PreviouslyPurchasedPage"],
        isSystem: true,
    },
};

export default widgetModule;
